<script setup>
import { ref, useTemplateRef, computed, onMounted, onUnmounted, getCurrentInstance, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import StreamService from '@/services/StreamService'
import CountDown from './CountDown.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCamera from '@/components/icon/IconCamera.vue'

// Store, route, notification
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const router = useRouter()

// Props
const props = defineProps({
	stream: {
		type: Object,
		required: true
	},
	compactMode: {
		required: true
	},
	mediaPulse: {
		type: Object,
		required: false,
		default: () => ({})
	},
})

// Template Refs
const thumbContainer = useTemplateRef('thumbContainer')

// Refs
const scopeAlive = ref(true)
const hasStreamThumb = ref(null)
const thumbUrl = ref(null)
const savedSchedulerConfig = ref(null)
const scheduleTimeActive = ref(false)
const thumbCreated = ref(false)
const meta = ref(null)
const liveStreamCountdownSecLeft = ref(null)

// Computed
const hasStreamScheduled = computed(() => {
	const conf = savedSchedulerConfig.value
	return conf && conf.mode === 'datetime' && conf.datetime
})

const countdownSecsLeft = computed(() => {
	const value = savedSchedulerConfig.value.datetime - Date.now()
	return Math.max(value, 0)
})


const shouldShowCounter = computed(() => {
	if (props.stream.type === 'scheduled' && hasStreamScheduled.value && !scheduleTimeActive.value && countdownSecsLeft.value) return true
	if (props.stream.type === 'live' && liveStreamCountdownSecLeft.value) return true
	return false
})

const endDate = computed(() => {
	if (props.stream.type === 'scheduled' && hasStreamScheduled.value && !scheduleTimeActive.value && countdownSecsLeft.value) return savedSchedulerConfig.value.datetime
	if (props.stream.type === 'live' && liveStreamCountdownSecLeft.value) return meta.value.countdownDate
	return null
})

const computedMessage = computed(() => {
	if (props.stream?.enabled) {
		if (props.mediaPulse?.alive) {
			return 'Waiting for frames'
		} else {
			if (props.stream.type === 'ipcam') return 'No camera source'
			if (props.stream.type === 'vod') return 'Static Media Files'
			if (props.stream.type === 'scheduled' && hasStreamScheduled.value && countdownSecsLeft.value) return ''
			return 'Waiting for stream'
		}
	}
	return 'Disabled Stream'
})

// Watchers
watch(() => props.mediaPulse, () => {
	// overwrite url if load balancer enabled
	const hostId = props.mediaPulse?.hostId
	if (hostId) {
		const staticPrefix = props.mediaPulse.staticPrefix
		const anc = document.createElement('a')
		anc.href = thumbUrl.value
		// if (/\w+-\d+/gi.test(hostId)) {
		if (/\w+-\d+/gi.test(hostId) || props.stream.pullUrl) {
			anc.hostname = `${hostId}.castr.io`
			if (!anc.pathname.includes('static/') && staticPrefix) {
				anc.pathname = anc.pathname.replace(props.stream.key, `static/${props.mediaPulse.name}`)
			}
		}
		thumbUrl.value = anc.href
		if (!thumbCreated.value && thumbUrl.value) {
			setThumbnail()
		}
	}
})

// Hooks
onMounted(async () => {
	const isWowzaRegion = !!props.stream?.region?.v2
	let hostname = 'dvr.castr.io'
	let prefixPath = ''
	if (isWowzaRegion) {
		hostname = 'preview.castr.io'
		prefixPath = 'preview/'
	}

	thumbUrl.value = `https://${hostname}/`
	if (prefixPath) {
		thumbUrl.value += prefixPath
	}

	const regionDepId = props.stream?.region?.identifierHaxr
	thumbUrl.value += `${props.stream.key}/preview.mp4?&region=${regionDepId}`

	// setTimeout(this.setThumbnail.bind(this), 1000);
	if (props.stream.type === 'scheduled') {
		await setupScheduling()
	}

	if (props.stream.type === 'live') {
		// Use cached store to prevent fetching metadata for every stream card
		meta.value = await store.dispatch('StreamCache/getMetadata', { 
			streamId: props.stream._id,
			videoId: false 
		})
		if (meta.value?.countdownDate) {
			liveStreamCountdownSecLeft.value = Math.max(new Date(meta.countdownDate) - Date.now(), 0)
		}
	}
})

onUnmounted(() => {
	scopeAlive.value = false
})


// Methods

const setThumbnail = () => {
	if (!scopeAlive.value) return

	// prepare a new video element to overalp existing ones
	let mediaElement
	const streamEnabled = props.stream?.enabled
	const streamAlive = props.mediaPulse?.alive

	if (streamEnabled && streamAlive && thumbContainer.value) {
		const isWowzaRegion = !!props.stream?.region?.v2
		if (isWowzaRegion) {
			// Wowza regions use a still thumbnail URL helper when available
			if (typeof getVideoThumbUrl === 'function') {
				const thumbUrl = getVideoThumbUrl()
				if (thumbUrl) {
					thumbCreated.value = true
					mediaElement = createImgInstance(thumbUrl, () => {
						thumbContainer.value.appendChild(mediaElement)
					})
				}
			}
		} else {
			const videoSrc = getVideoPreviewUrl()
			mediaElement = createVideoInstance(videoSrc)
			if (mediaElement) {
				thumbCreated.value = true
				thumbContainer.value.appendChild(mediaElement)
			}
		}
	}

	// add few secs delay to make the video chunk appear
	if (mediaElement) {
		setTimeout(() => {
			mediaElement.className = mediaElement.className + ' ready'

			// reschedule thumb setup
			scheduleSetup.call(this, mediaElement.tagName.toLowerCase())
		}, 3000)
	} else {
		// reschedule thumb setup
		scheduleSetup.call(this)
	}

	function scheduleSetup(thumbType = 'video') {
		// setTimeout(setThumbnail.bind(this), 8000)
		if (!thumbContainer) return

		// remove old video thumbs
		const streamEnabled = props.stream.enabled

		// recheck if placeholder text has to appear instead of video thumb
		hasStreamThumb.value = streamEnabled && props.mediaPulse?.alive

		const mediaEls = thumbContainer.value?.getElementsByTagName(thumbType)
		if (mediaEls) {
			Array.prototype.forEach.call(mediaEls, mediaEl => {
			// remove video if stream is offline or is older video
			if (!hasStreamThumb || mediaEl !== mediaElement) {
				if (thumbContainer.value) {
					thumbContainer.value.removeChild(mediaEl)
				}
			}
		})
		}
	}
}

const getVideoPreviewUrl = () => {
	if (!props.mediaPulse || !props.mediaPulse.alive) return
	const randomkey = Math.random().toString().slice(2)
	const vidUrl = thumbUrl.value + `&rand=${randomkey}`
	return vidUrl
}

const setupScheduling = async () => {
	// Use cached store to prevent duplicate calls
	const schedulerConfig = await store.dispatch('StreamCache/getSchedulerConfig', props.stream._id) || {}
	savedSchedulerConfig.value = schedulerConfig

}

const onScheduleCountdownTick = (value, forceEnded) => {
	if (!savedSchedulerConfig.value || props.stream.type !== 'scheduled') return
	let offset = 0
	if (!forceEnded) {
		offset = savedSchedulerConfig.value.datetime - Date.now()
		offset /= 1000
		if (value) offset = value.totalSeconds
	}
	scheduleTimeActive.value = offset <= 0
}

function createVideoInstance(videoSrc) {
	if (!videoSrc) return
	const vid = document.createElement('video')
	vid.src = videoSrc
	vid.className = 'h-full w-full object-cover z-10 relative'
	return vid
}

function createImgInstance(imgSrc, callback) {
	if (!imgSrc) return
	const img = document.createElement('img')
	img.src = imgSrc
	img.className = 'h-full object-cover z-10 relative'
	img.onload = () => void callback()
	return img
}
</script>

<template>
	<div id="thumb-container" ref="thumbContainer" :class="{ compact: compactMode }" class="relative w-full h-full">
		<div v-show="!hasStreamThumb" class="placeholder w-full h-full flex flex-col items-center justify-center text-surface-5 absolute top-0 left-0"
			:class="props.compactMode ? 'gap-0.5 px-1 text-center' : 'gap-2'">
			<icon-base :width="props.compactMode ? 16 : 28" :height="props.compactMode ? 16 : 28" :width-view-box="16" :height-view-box="14" class="opacity-50">
				<icon-camera />
			</icon-base>
			<span class="whitespace-nowrap" :class="props.compactMode ? 'text-[10px] leading-tight' : ''">{{ computedMessage }}</span>
			<span class="text-sm text-surface-7" v-if="!props.stream.enabled && !props.compactMode">Please enable</span>
			<CountDown v-if="shouldShowCounter && !props.compactMode" :endDate="endDate" @progress="onScheduleCountdownTick">
				Stream will go Live in <br>
			</CountDown>
		</div>
	</div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

.compact.offline.enabled .thumb::after {
	font-size: 14px;
	color: var(--c-dark-5);
	content: "Waiting for stream";
	text-align: center;
}
</style>